/**
 * Inquiries API - Request information and inquiry management
 */

import { throwApiError } from './client-error';
import { getAttribution } from '../lib/attribution';

// =============================================================================
// Types
// =============================================================================

export type InquirySource = 'dual_option_hosted' | 'remote_request' | 'general' | 'booking_question' | 'contact_form' | 'booking_closed';
export type InquiryStatus = 'pending' | 'in_review' | 'responded' | 'converted' | 'closed';

export interface InquiryBase {
  showId?: string;
  showName?: string;
  tourStopId?: string;
  source: InquirySource;
  message?: string;
}

export interface HostedClassInquiry extends InquiryBase {
  source: 'dual_option_hosted';
  // Venue details are no longer collected from the studio at submit time
  // (SDA-158) — SDA team locates the venue on the studio's behalf. Kept
  // optional on the type so admins can still supply venue info later.
  venueDetails?: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    capacity: number;
    parkingInfo?: string;
  };
  preferredDates: string[]; // Up to 3 ISO date strings
  studioWillTravel: boolean;
}

export interface RemoteStudioInquiry extends InquiryBase {
  source: 'remote_request';
  preferredCities: string[];
  notifyWhenCloser: boolean;
}

export interface GeneralInquiry extends InquiryBase {
  source: 'general';
}

export interface BookingClosedInquiry extends InquiryBase {
  source: 'booking_closed';
  preferredDates: string[];
}

export interface BookingQuestionInquiry {
  bookingId: string;
  source: 'booking_question';
  message: string;
}

export type CreateInquiryRequest = HostedClassInquiry | RemoteStudioInquiry | GeneralInquiry | BookingClosedInquiry | BookingQuestionInquiry;

export interface InquiryResponse {
  id: string;
  token: string; // Public tracking token
  status: InquiryStatus;
  createdAt: string;
  estimatedResponseTime: string;
}

export interface InquiryStatusResponse {
  id: string;
  status: InquiryStatus;
  showName: string;
  source: InquirySource;
  message?: string;
  createdAt: string;
  updatedAt: string;
  respondedAt?: string;
  responseMessage?: string;
  bookingLink?: string;
  estimatedResponseTime: string;
  slaStatus: 'on_track' | 'warning' | 'breached';
}

export interface StudioInquiry {
  id: string;
  token: string;
  showId: string;
  showName: string;
  showLogoUrl?: string;
  tourStopId?: string;
  venueName?: string;
  venueCity?: string;
  venueState?: string;
  source: InquirySource;
  status: InquiryStatus;
  message?: string;
  createdAt: string;
  updatedAt: string;
  respondedAt?: string;
  responseMessage?: string;
}

export interface BookingInquirySummary {
  id: string;
  status: InquiryStatus;
  inquiry_source: InquirySource;
  message: string;
  response_message: string | null;
  responded_at: string | null;
  created_at: string;
  status_token: string;
}

// =============================================================================
// Auth Helper
// =============================================================================

/**
 * Get the auth token from localStorage
 * Must match the storage key used by AuthContext
 */
function getAuthToken(): string | null {
  try {
    const tokensJson = localStorage.getItem('sda_auth_tokens');
    if (!tokensJson) return null;
    const tokens = JSON.parse(tokensJson) as { access_token: string; expires_at: number };
    // Check if token is expired
    if (tokens.expires_at * 1000 < Date.now()) {
      return null;
    }
    return tokens.access_token;
  } catch {
    return null;
  }
}

// =============================================================================
// API Functions
// =============================================================================

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Submit a new inquiry (authenticated user)
 */
export async function submitInquiry(data: CreateInquiryRequest): Promise<InquiryResponse> {
  const token = getAuthToken();

  const response = await fetch(`${API_BASE}/api/inquiries`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    // SDA-267: attach stored campaign attribution out-of-band; the server
    // validates and stamps it (absent/invalid never blocks the inquiry).
    body: JSON.stringify({ ...data, attribution: getAttribution() ?? undefined }),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: {} }));
    throwApiError(response, result, 'Failed to submit inquiry');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Submit a booking question (authenticated user)
 */
export async function submitBookingQuestion(data: { bookingId: string; message: string }): Promise<InquiryResponse> {
  const token = getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE}/api/inquiries`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      bookingId: data.bookingId,
      source: 'booking_question',
      message: data.message,
    }),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: {} }));
    throwApiError(response, result, 'Failed to submit question');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Submit an inquiry as a guest (no auth required)
 */
export type GuestInquiryRequest = CreateInquiryRequest & {
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  studioName: string;
  /** Optional SMS/text-message consent opt-in (A2P/10DLC). */
  sms_consent?: boolean;
};

export async function submitGuestInquiry(data: GuestInquiryRequest): Promise<InquiryResponse> {
  const response = await fetch(`${API_BASE}/api/inquiries/guest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    // SDA-267: attach stored campaign attribution out-of-band; the server
    // validates and stamps it (absent/invalid never blocks the inquiry).
    body: JSON.stringify({ ...data, attribution: getAttribution() ?? undefined }),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: {} }));
    throwApiError(response, result, 'Failed to submit inquiry');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Get inquiry status by public token (no auth required)
 */
export async function getInquiryStatus(token: string): Promise<InquiryStatusResponse> {
  const response = await fetch(`${API_BASE}/api/inquiries/status/${token}`);

  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: {} }));
    throwApiError(response, result, 'Inquiry not found');
  }

  const result = await response.json();
  return result.data;
}

/**
 * Get studio's inquiries (authenticated)
 */
export async function getMyInquiries(): Promise<StudioInquiry[]> {
  const token = getAuthToken();
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${API_BASE}/api/inquiries/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: {} }));
    throwApiError(response, result, 'Failed to load inquiries');
  }

  const result = await response.json();
  return result.data;
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Format inquiry source for display
 */
export function formatInquirySource(source: InquirySource): string {
  switch (source) {
    case 'dual_option_hosted':
      return 'Hosted Class Request';
    case 'remote_request':
      return 'Notify When Closer';
    case 'general':
      return 'General Inquiry';
    case 'booking_question':
      return 'Booking Question';
    case 'contact_form':
      return 'Contact Form';
    case 'booking_closed':
      return 'Booking Closed Request';
    default:
      return source;
  }
}

/**
 * Format inquiry status for display
 */
export function formatInquiryStatus(status: InquiryStatus): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'in_review':
      return 'In Review';
    case 'responded':
      return 'Responded';
    case 'converted':
      return 'Converted to Booking';
    case 'closed':
      return 'Closed';
    default:
      return status;
  }
}

/**
 * Get status badge color
 */
export function getStatusColor(status: InquiryStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-warning-100 text-warning-700';
    case 'in_review':
      return 'bg-info-100 text-info-700';
    case 'responded':
      return 'bg-success-100 text-success-700';
    case 'converted':
      return 'bg-primary-100 text-primary-700';
    case 'closed':
      return 'bg-warm-100 text-warm-600';
    default:
      return 'bg-warm-100 text-warm-600';
  }
}

/**
 * Get SLA status color
 */
export function getSlaColor(slaStatus: 'on_track' | 'warning' | 'breached'): string {
  switch (slaStatus) {
    case 'on_track':
      return 'text-success-600';
    case 'warning':
      return 'text-warning-600';
    case 'breached':
      return 'text-danger-600';
    default:
      return 'text-warm-600';
  }
}

// =============================================================================
// Interest Subscription (no auth required)
// =============================================================================

export async function subscribeInterest(data: { email: string; name: string; show_id: string; city_id?: string }): Promise<void> {
  const response = await fetch(`${API_BASE}/api/interest/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: {} }));
    throwApiError(response, result, 'Failed to subscribe');
  }
}

// =============================================================================
// Contact Form (no auth required)
// =============================================================================

export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  message: string;
  /** Optional SMS/text-message consent opt-in (A2P/10DLC). */
  sms_consent?: boolean;
  context?: {
    showId?: string;
    showName?: string;
    tourStopId?: string;
    venueName?: string;
    venueCity?: string;
    venueState?: string;
    selectedClass?: string;
    selectedDate?: string;
    selectedTimeSlot?: string;
    pricingTier?: string;
    distanceMiles?: number;
    funnelPosition?: string;
    pageUrl?: string;
  };
}

/**
 * Submit a contact/help form - no auth required
 */
export async function submitContactForm(data: ContactFormData): Promise<void> {
  const token = getAuthToken();

  const response = await fetch(`${API_BASE}/api/contact`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({ error: {} }));
    throwApiError(response, result, 'Failed to submit. Please try again.');
  }
}
